import { __spreadArrays } from "tslib";
import { combine, isEmptyObject, limitModification, timeStampNow, currentDrift, display, addMonitoringMessage, relativeNow, createEventRateLimiter, getCookie, } from '@datadog/browser-core';
import { RumEventType, } from '../rawRumEvent.types';
import { LifeCycleEventType } from './lifeCycle';
import { RumSessionPlan } from './rumSession';
var SessionType;
(function (SessionType) {
    SessionType["SYNTHETICS"] = "synthetics";
    SessionType["USER"] = "user";
})(SessionType || (SessionType = {}));
var VIEW_EVENTS_MODIFIABLE_FIELD_PATHS = [
    // Fields with sensitive data
    'view.url',
    'view.referrer',
    'action.target.name',
    'error.message',
    'error.stack',
    'error.resource.url',
    'resource.url',
];
var OTHER_EVENTS_MODIFIABLE_FIELD_PATHS = __spreadArrays(VIEW_EVENTS_MODIFIABLE_FIELD_PATHS, [
    // User-customizable field
    'context',
]);
export function startRumAssembly(applicationId, configuration, lifeCycle, session, parentContexts, urlContexts, getCommonContext) {
    var _a;
    var reportError = function (error) {
        lifeCycle.notify(LifeCycleEventType.RAW_ERROR_COLLECTED, { error: error });
    };
    var eventRateLimiters = (_a = {},
        _a[RumEventType.ERROR] = createEventRateLimiter(RumEventType.ERROR, configuration.maxErrorsPerMinute, reportError),
        _a[RumEventType.ACTION] = createEventRateLimiter(RumEventType.ACTION, configuration.maxActionsPerMinute, reportError),
        _a);
    lifeCycle.subscribe(LifeCycleEventType.RAW_RUM_EVENT_COLLECTED, function (_a) {
        var _b;
        var startTime = _a.startTime, rawRumEvent = _a.rawRumEvent, domainContext = _a.domainContext, savedCommonContext = _a.savedCommonContext, customerContext = _a.customerContext;
        var viewContext = parentContexts.findView(startTime);
        var urlContext = urlContexts.findUrl(startTime);
        if (session.isTracked() && viewContext && urlContext && viewContext.session.id === session.getId()) {
            var actionContext = parentContexts.findAction(startTime);
            var commonContext = savedCommonContext || getCommonContext();
            var rumContext = {
                _dd: {
                    format_version: 2,
                    drift: currentDrift(),
                    session: {
                        plan: session.hasReplayPlan() ? RumSessionPlan.REPLAY : RumSessionPlan.LITE,
                    },
                },
                application: {
                    id: applicationId,
                },
                date: timeStampNow(),
                service: configuration.service,
                session: {
                    // must be computed on each event because synthetics instrumentation can be done after sdk execution
                    type: getSessionType(),
                },
                synthetics: getSyntheticsContext(),
            };
            var serverRumEvent = (needToAssembleWithAction(rawRumEvent)
                ? combine(rumContext, urlContext, viewContext, actionContext, rawRumEvent)
                : combine(rumContext, urlContext, viewContext, rawRumEvent));
            serverRumEvent.context = combine(commonContext.context, customerContext);
            if (!('has_replay' in serverRumEvent.session)) {
                ;
                serverRumEvent.session.has_replay = commonContext.hasReplay;
            }
            if (!isEmptyObject(commonContext.user)) {
                ;
                serverRumEvent.usr = commonContext.user;
            }
            if (shouldSend(serverRumEvent, configuration.beforeSend, domainContext, eventRateLimiters)) {
                if (isEmptyObject(serverRumEvent.context)) {
                    delete serverRumEvent.context;
                }
                if (typeof serverRumEvent.date !== 'number') {
                    addMonitoringMessage('invalid date', {
                        debug: {
                            eventType: serverRumEvent.type,
                            eventTimeStamp: serverRumEvent.date,
                            eventRelativeTime: Math.round(startTime),
                            timeStampNow: timeStampNow(),
                            relativeNow: Math.round(relativeNow()),
                            drift: currentDrift(),
                        },
                    });
                }
                if (serverRumEvent.session.has_replay && ((_b = serverRumEvent._dd.session) === null || _b === void 0 ? void 0 : _b.plan) === RumSessionPlan.LITE) {
                    addMonitoringMessage('lite session with replay', {
                        debug: {
                            eventType: serverRumEvent.type,
                            viewId: serverRumEvent.view.id,
                            sessionId: serverRumEvent.session.id,
                            inMemoryPlan: session.getInMemoryPlan(),
                            event: serverRumEvent,
                            _dd_s: getCookie('_dd_s'),
                        },
                    });
                }
                lifeCycle.notify(LifeCycleEventType.RUM_EVENT_COLLECTED, serverRumEvent);
            }
        }
    });
}
function shouldSend(event, beforeSend, domainContext, eventRateLimiters) {
    var _a;
    if (beforeSend) {
        var result = limitModification(event, event.type === RumEventType.VIEW ? VIEW_EVENTS_MODIFIABLE_FIELD_PATHS : OTHER_EVENTS_MODIFIABLE_FIELD_PATHS, function (event) { return beforeSend(event, domainContext); });
        if (result === false && event.type !== RumEventType.VIEW) {
            return false;
        }
        if (result === false) {
            display.warn("Can't dismiss view events using beforeSend!");
        }
    }
    var rateLimitReached = (_a = eventRateLimiters[event.type]) === null || _a === void 0 ? void 0 : _a.isLimitReached();
    return !rateLimitReached;
}
function needToAssembleWithAction(event) {
    return [RumEventType.ERROR, RumEventType.RESOURCE, RumEventType.LONG_TASK].indexOf(event.type) !== -1;
}
function getSessionType() {
    return navigator.userAgent.indexOf('DatadogSynthetics') === -1 && !getSyntheticsContext()
        ? SessionType.USER
        : SessionType.SYNTHETICS;
}
function getSyntheticsContext() {
    var testId = window._DATADOG_SYNTHETICS_PUBLIC_ID;
    var resultId = window._DATADOG_SYNTHETICS_RESULT_ID;
    if (typeof testId === 'string' && typeof resultId === 'string') {
        return {
            test_id: testId,
            result_id: resultId,
        };
    }
}
//# sourceMappingURL=assembly.js.map